import api from '@pray/shared/utils/api';

import type { AudienceAnalyticsApiResponse, AudienceAnalyticsData } from './types';

interface GetAudienceAnalyticsParams {
  artistId: string;
  startDate?: string;
  endDate?: string;
  bustCache?: boolean;
}

/**
 * Maps API response to client-friendly format
 */
function mapAudienceAnalytics(data: AudienceAnalyticsApiResponse): AudienceAnalyticsData {
  return {
    artistName: data.artist_name,
    totalFollows: data.total_follows ?? 0,
    totalFavorites: data.total_favorites ?? 0,
    totalShares: data.total_shares ?? 0,
    totalArtistPageView: data.total_artist_page_view ?? 0,
    totalGivingClick: data.total_giving_click ?? 0,
    dateRange: {
      startDate: data.date_range?.start_date ?? '',
      endDate: data.date_range?.end_date ?? '',
    },
    // New fields for enhanced audience analytics
    listenFollowers: {
      total: data.listen_followers?.total ?? 0,
      sparklineData: (data.listen_followers?.daily_data ?? []).map((d) => ({
        date: d.date,
        value: d.value,
      })),
    },
    watchFollowers: {
      total: data.watch_followers?.total ?? 0,
      sparklineData: (data.watch_followers?.daily_data ?? []).map((d) => ({
        date: d.date,
        value: d.value,
      })),
    },
    ageDistribution: (data.age_distribution ?? []).map((d) => ({
      label: d.age_group,
      value: d.percentage,
    })),
    genderDistribution: (data.gender_distribution ?? []).map((d) => ({
      label: d.gender,
      value: d.percentage,
    })),
    religionDistribution: (data.religion_distribution ?? []).map((d) => ({
      label: d.label,
      value: d.percentage,
    })),
    politicalDistribution: (data.political_distribution ?? []).map((d) => ({
      label: d.label,
      value: d.percentage,
    })),
    ethnicityDistribution: (data.ethnicity_distribution ?? []).map((d) => ({
      label: d.label,
      value: d.percentage,
    })),
    salaryDistribution: (data.salary_distribution ?? []).map((d) => ({
      label: d.label,
      value: d.percentage,
    })),
    educationDistribution: (data.education_distribution ?? []).map((d) => ({
      label: d.label,
      value: d.percentage,
    })),
    maritalStatusDistribution: (data.marital_status_distribution ?? []).map((d) => ({
      label: d.label,
      value: d.percentage,
    })),
    topUSLocations: data.top_us_locations ?? [],
    stateDistribution: data.state_distribution ?? {},
  };
}

/**
 * Get audience analytics for an artist
 */
export async function getAudienceAnalytics({
  artistId,
  startDate,
  endDate,
  bustCache,
}: GetAudienceAnalyticsParams): Promise<AudienceAnalyticsData> {
  if (!artistId) throw new Error('Artist ID is required');

  const params = new URLSearchParams();
  if (startDate) params.append('startDate', startDate);
  if (endDate) params.append('endDate', endDate);
  if (bustCache) params.append('bustCache', 'true');

  const queryString = params.toString();
  const url = `/web/studio/artists/${artistId}/analytics/audience${queryString ? `?${queryString}` : ''}`;

  const response = await api.executeGetRequest<{ data: AudienceAnalyticsApiResponse }>(url);

  return mapAudienceAnalytics(response.data.data);
}
